import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Typography, Descriptions, Spin, Button, Divider, message } from 'antd';
import { Link } from 'react-router-dom';
import api from '../../utils/axios';
import type { Claim, Client } from '../../types/api';

interface InsuredsTabProps {
  client: Client | undefined;
  currentClaimId: string;
}

const { Title, Text } = Typography;

const InsuredsTab: React.FC<InsuredsTabProps> = ({ client, currentClaimId }) => {
  const [claims, setClaims] = useState<Claim[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchClaims = async () => {
      if (!client?.id) return;
      setLoading(true);
      try {
        const response = await api.get(`/claims?clientId=${client.id}&limit=100`);
        if (response.data.success) {
          setClaims(response.data.data.filter((claim: Claim) => claim.id !== currentClaimId));
        } else {
          message.error('Failed to load claims for client');
        }
      } catch (err) {
        message.error('Failed to load claims for client');
      } finally {
        setLoading(false);
      }
    };
    fetchClaims();
  }, [client, currentClaimId]);

  if (!client) {
    return <div>No insured client data found for this claim.</div>;
  }

  return (
    <div>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card title={<div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
  <span>Insured Details</span>
  <Link to={`/clients/${client.uuid || client.id}`} style={{marginLeft:'auto'}}>
    <Button type="primary" size="small">View Full Profile</Button>
  </Link>
</div>} className="mb-4">
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Type">
                <Text strong>{client.type}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Text>{client.status}</Text>
              </Descriptions.Item>
              {client.type === 'COMPANY' ? (
                <Descriptions.Item label="Company Name">
                  <Text strong>{client.companyName}</Text>
                </Descriptions.Item>
              ) : (
                <>
                  <Descriptions.Item label="First Name">
                    <Text>{client.firstName}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Last Name">
                    <Text>{client.lastName}</Text>
                  </Descriptions.Item>
                </>
              )}
              <Descriptions.Item label="Email">
                <Text>{client.email || 'Not provided'}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Phone">
                <Text>{client.phone || 'Not provided'}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Address" span={2}>
                <Text>{client.address || 'Not provided'}</Text>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
      </Row>
      <Divider orientation="left">Other Claims for this Insured</Divider>
      {loading ? (
        <Spin />
      ) : claims.length === 0 ? (
        <div>No other claims found for this insured.</div>
      ) : (
        <Row gutter={[16, 16]}>
          {claims.map((claim) => (
            <Col span={12} key={claim.id}>
              <Card size="small" title={`Claim #${claim.file_number || claim.claimNumber}`}
                extra={<Link to={`/claims/${claim.file_number || claim.id}`}>View</Link>}>
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Type">
                    <Text>{claim.type}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Status">
                    <Text>{claim.status}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Date of Loss">
                    <Text>{new Date(claim.dateOfLoss).toLocaleDateString()}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Description">
                    <Text>{claim.description}</Text>
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
};

export default InsuredsTab;
